import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { propertyGroups } from '../api/endpoints.js';
import {
  propertyGroupFormInput,
  type PropertyGroupDetail,
  type PropertyGroupFormInput,
} from '../api/schemas.js';
import { PropertyGroupFields } from '../forms/property-group-fields.js';

const CREATE_DEFAULTS: PropertyGroupFormInput = {
  code: '',
  name: '',
  description: '',
  status: 'ACTIVE',
  propertyIds: [],
};

function defaultsFrom(group: PropertyGroupDetail): PropertyGroupFormInput {
  return {
    code: group.code,
    name: group.name,
    description: group.description ?? '',
    status: group.status,
    propertyIds: group.propertyIds,
  };
}

export function PropertyGroupFormPage(): React.JSX.Element {
  const propertyGroupId = useParams().propertyGroupId;
  const isEdit = propertyGroupId !== undefined;
  return (
    <PermissionGuard
      permission={isEdit ? PERMISSIONS.PROPERTY_GROUP_UPDATE : PERMISSIONS.PROPERTY_GROUP_CREATE}
      fallback={<NotPermitted />}
    >
      {isEdit ? <EditGroup id={propertyGroupId} /> : <CreateGroup />}
    </PermissionGuard>
  );
}

function CreateGroup(): React.JSX.Element {
  const navigate = useNavigate();
  const create = propertyGroups.useCreate();
  const form = useAppForm<PropertyGroupFormInput>({
    schema: propertyGroupFormInput,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'Property group created',
    onSubmit: async (values) => {
      const result = await create.mutateAsync(values);
      void navigate(`/property-groups/${result.id}`);
    },
  });
  return (
    <FormPage
      title="New property group"
      description="Create a named collection of properties."
      backTo="/property-groups"
      backLabel="Property groups"
      submitLabel="Create property group"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <PropertyGroupFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
      />
    </FormPage>
  );
}

function EditGroup({ id }: { readonly id: string }): React.JSX.Element {
  const query = propertyGroups.useItem(id);
  if (query.isPending) return <LoadingState label="Loading property group" />;
  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => void query.refetch()}
      />
    );
  }
  return <EditGroupForm id={id} group={query.data} />;
}

function EditGroupForm({
  id,
  group,
}: {
  readonly id: string;
  readonly group: PropertyGroupDetail;
}): React.JSX.Element {
  const navigate = useNavigate();
  const update = propertyGroups.useUpdate();
  const form = useAppForm<PropertyGroupFormInput>({
    schema: propertyGroupFormInput,
    defaultValues: defaultsFrom(group),
    successMessage: 'Property group updated',
    onSubmit: async (values) => {
      await update.mutateAsync({
        id,
        payload: { ...values, version: group.version },
      });
      void navigate(`/property-groups/${id}`);
    },
  });
  return (
    <FormPage
      title={group.name}
      description="Edit the property group."
      backTo={`/property-groups/${id}`}
      backLabel={group.name}
      submitLabel="Save changes"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <PropertyGroupFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
      />
    </FormPage>
  );
}
